import * as XLSX from "xlsx";

/**
 * Exports hierarchical data to an Excel file.
 *
 * Each top‑level node (parent) will be exported to its own sheet (named after the parent).
 * In each sheet, every row represents a path from the top parent to a leaf node.
 * The parent appears in column A, its child in column B, its grandchild in column C, etc.
 *
 * @param {Object|Array} hierarchies - A single hierarchy object or an array of hierarchies.
 */
export function exportHierarchyToExcel(hierarchies) {
    // Create a new workbook
    const workbook = XLSX.utils.book_new();

    // Ensure hierarchies is an array
    const topNodes = Array.isArray(hierarchies) ? hierarchies : [hierarchies];

    topNodes.forEach((topNode) => {
        // Generate a valid sheet name based on the top node's name.
        // Excel sheet names are limited to 31 characters.
        let sheetName = topNode.name || "Sheet";
        sheetName = sheetName.substring(0, 31);

        // Get all paths from the top node down to each leaf.
        // Each path is an array of node names.
        const paths = getPaths(topNode);

        // Create a worksheet from the array-of-arrays.
        const worksheet = XLSX.utils.aoa_to_sheet(paths);

        // Append the worksheet to the workbook under the desired sheet name.
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    });

    // Write the workbook to a file. In a browser this will prompt a download.
    XLSX.writeFile(workbook, "hierarchy.xlsx");
}

/**
 * Recursively traverses a hierarchy node and returns an array of paths.
 *
 * Each path is an array of node names starting at the top parent and
 * ending at a leaf node.
 *
 * @param {Object} node - A node in the hierarchy.
 * @param {Array} [currentPath=[]] - The current path (used during recursion).
 * @returns {Array} An array of paths (each path is an array of strings).
 */
function getPaths(node, currentPath = []) {
    // Add the current node's name to the path.
    const newPath = [...currentPath, node.name];

    // If the node has no children, return the current path as the only path.
    if (!node.children || node.children.length === 0) {
        return [newPath];
    }

    // Otherwise, for each child, recursively get its paths.
    let paths = [];
    node.children.forEach((child) => {
        const childPaths = getPaths(child, newPath);
        paths = paths.concat(childPaths);
    });

    return paths;
}
